import type {PatchStatus} from '@github-ui/diff-file-helpers'

import {NO_FILE_EXTENSION} from './FileFilter'

export type SummaryDelta = {
  path: string
  pathDigest: string
}

export type AnnotationLevels = 'NOTICE' | 'WARNING' | 'FAILURE'

export type DiffDelta = SummaryDelta & {
  changeType: PatchStatus
  totalCommentsCount?: number
  highestAnnotationLevel?: AnnotationLevels
  isSymlink?: boolean
}

/**
 * Compare two strings with case-first ordering. ZZZ.txt will come before aaa.txt with this strategy.
 */
export function caseFirstCompare(a: string, b: string): number {
  // Handle the specific case where one string is a prefix of the other
  if (a.startsWith(b) && a !== b) {
    return -1 // The longer string (a) comes first
  }
  if (b.startsWith(a) && a !== b) {
    return 1 // The longer string (b) comes first
  }

  // For all other cases, use standard string comparison (keeps uppercase before lowercase)
  if (a < b) return -1
  if (a > b) return 1
  return 0
}

function compareDirectoryNames<T extends SummaryDelta>(a: DirectoryNode<T>, b: DirectoryNode<T>) {
  return caseFirstCompare(a.name, b.name)
}

function compareFileNames<T extends SummaryDelta>(a: FileNode<T>, b: FileNode<T>) {
  return caseFirstCompare(a.fileName!, b.fileName!)
}

export class FileNode<T extends SummaryDelta> {
  diff: T
  filePath: string
  fileName: string | undefined
  directoryParts: string[]
  sortOrder: number

  constructor(diff: T, sortOrder: number) {
    this.diff = diff
    this.filePath = diff.path
    const pathParts = this.filePath.split('/')
    this.fileName = pathParts[pathParts.length - 1]
    this.directoryParts = pathParts.slice(0, pathParts.length - 1)
    this.sortOrder = sortOrder
  }
}

export class DirectoryNode<T extends SummaryDelta> {
  directories: Array<DirectoryNode<T>> = []
  files: Array<FileNode<T>> = []

  #directoriesByName = new Map<string, DirectoryNode<T>>()

  name: string
  path: string
  sortOrder: number

  constructor(name: string, path: string, sortOrder: number) {
    this.name = name
    this.path = path
    this.sortOrder = sortOrder
  }

  getOrCreateDirectory(name: string, sortOrder: number) {
    let directory = this.#directoriesByName.get(name)

    if (!directory) {
      const pathPrefix = this.path ? `${this.path}/` : ''
      directory = new DirectoryNode(name, `${pathPrefix}${name}`, sortOrder)
      this.directories.push(directory)
      this.#directoriesByName.set(name, directory)
    }

    return directory
  }

  sort() {
    this.directories.sort(compareDirectoryNames)
    this.files.sort(compareFileNames)

    for (const subDirectory of this.directories) {
      subDirectory.sort()
    }
  }
}

export function getFileTree<T extends SummaryDelta>(summaryDeltas: readonly T[]): DirectoryNode<T> {
  const baseNode = new DirectoryNode<T>('', '', Number.MAX_SAFE_INTEGER) // root directory with high sort order
  const files = summaryDeltas.map((diff, index) => new FileNode<T>(diff, index))

  for (const file of files) {
    let directory = baseNode
    for (const directoryPath of file.directoryParts) {
      // Create directory with current sort order, but we'll update it later
      directory = directory.getOrCreateDirectory(directoryPath, file.sortOrder)

      // Always update directory's sort order to the minimum of current and file's
      directory.sortOrder = Math.min(directory.sortOrder, file.sortOrder)
    }
    directory.files.push(file)
  }

  // Propagate minimum sort orders upward to parent directories
  updateDirectorySortOrders(baseNode) // used for sort order sorting
  baseNode.sort() // used for client-side sorting

  return baseNode
}

// This function ensures that each directory's sort order is the minimum of its own and its children's sort orders
// This is necessary to ensure that the sort order reflects the correct hierarchy and file order.
function updateDirectorySortOrders<T extends SummaryDelta>(node: DirectoryNode<T>) {
  // Recursively update all subdirectories
  for (const subDir of node.directories) {
    updateDirectorySortOrders(subDir)
  }

  // Calculate the minimum sort order for this directory
  const subDirectorySortOrders = node.directories.map(d => d.sortOrder)
  const fileSortOrders = node.files.map(f => f.sortOrder)
  node.sortOrder = Math.min(node.sortOrder, ...subDirectorySortOrders, ...fileSortOrders)
}

type FileProps = string | undefined | {newPath?: string | null | undefined; oldPath?: string | null | undefined}

export function getFileExtension(file: FileProps): string {
  let path: string | null | undefined
  if (typeof file === 'string') {
    path = file
  } else {
    path = file?.newPath || file?.oldPath
  }
  const lastPeriodIndex = path?.lastIndexOf('.')
  if (!path) {
    return ''
  }
  if (!lastPeriodIndex || lastPeriodIndex < 0) {
    return NO_FILE_EXTENSION
  }

  return `.${path.substring(lastPeriodIndex + 1)}`
}

export function getFileExtensionCounts(
  diffs: Readonly<Array<{path: string}>> | Array<{path: string}>,
): Record<string, number> {
  const fileExtensionCounts: Record<string, number> = {}
  diffs.map(diff => {
    const fileExtension = getFileExtension(diff.path)
    if (fileExtensionCounts[fileExtension] !== undefined) {
      fileExtensionCounts[fileExtension] += 1
    } else {
      fileExtensionCounts[fileExtension] = 1
    }
  })

  return fileExtensionCounts
}
